/** Pure host input state. No DOM, native player, network or account dependency. */
export type ControllerAction = 'up' | 'down' | 'left' | 'right' | 'select' | 'back' | 'guide' | 'toggle' | 'seek-back' | 'seek-forward';
export type ControllerSample = { identity: string; mapping: string; connected: boolean; buttons: readonly boolean[]; axes: readonly number[] };
const buttons: ReadonlyArray<[number, ControllerAction]> = [[0, 'select'], [1, 'back'], [2, 'guide'], [9, 'toggle'], [4, 'seek-back'], [5, 'seek-forward']];
const directions: ReadonlyArray<[number, ControllerAction]> = [[12, 'up'], [13, 'down'], [14, 'left'], [15, 'right']];
export class ControllerInput {
  private identity = '';
  private armed = false;
  private held = new Set<ControllerAction>();
  private direction: ControllerAction | null = null;
  private analogDirection: ControllerAction | null = null;
  private nextRepeat = 0;
  reset() { this.identity = ''; this.armed = false; this.held.clear(); this.direction = null; this.analogDirection = null; this.nextRepeat = 0; }
  sample(value: ControllerSample | null, now: number, active: boolean): ControllerAction[] {
    if (!active || !value?.connected || value.mapping !== 'standard' || !Number.isFinite(now)) { this.reset(); return []; }
    if (value.identity !== this.identity) { this.reset(); this.identity = value.identity; }
    const pressed = new Set(buttons.filter(([i]) => value.buttons[i]).map(([, action]) => action));
    const digital = directions.filter(([i]) => value.buttons[i]);
    const x = Number.isFinite(value.axes[0]) ? value.axes[0] : 0, y = Number.isFinite(value.axes[1]) ? value.axes[1] : 0;
    const magnitude = Math.max(Math.abs(x), Math.abs(y));
    // Separate engage/release thresholds keep noisy sticks from generating
    // a new first-press event on every frame around the engage threshold.
    if (magnitude < .25) this.analogDirection = null;
    else if (magnitude >= .6) {
      const previous = this.analogDirection;
      const stillHeld = previous === 'right' ? x >= .25 : previous === 'left' ? x <= -.25
        : previous === 'down' ? y >= .25 : previous === 'up' ? y <= -.25 : false;
      if (!stillHeld) this.analogDirection = Math.abs(x) > Math.abs(y) ? x > 0 ? 'right' : 'left' : y > 0 ? 'down' : 'up';
    }
    // Opposite D-pad buttons cancel; a diagonal resolves to one direction.
    const opposed = (value.buttons[12] && value.buttons[13]) || (value.buttons[14] && value.buttons[15]);
    const direction = opposed ? null : digital[0]?.[1] ?? this.analogDirection;
    if (!this.armed) {
      if (!pressed.size && !digital.length && Math.max(Math.abs(x), Math.abs(y)) < .25) this.armed = true;
      return [];
    }
    const actions = [...pressed].filter(action => !this.held.has(action));
    this.held = pressed;
    if (direction !== this.direction) { this.direction = direction; this.nextRepeat = now + 350; if (direction) actions.push(direction); }
    else if (direction && now >= this.nextRepeat) { actions.push(direction); this.nextRepeat = now + 130; }
    // No catch-up bursts after a stalled frame. Confirm/play/seek are edge-only.
    return actions.length > 1 ? [] : actions;
  }
}
